import useI18n from '@/shared/hooks/usei18n'
import { BreadcrumbsBar, Button, Card, CardBody, H2, PromoBanner } from '@/common'
import { Container, LoadingScreen } from '@/shared/components'
import { NetMassDisplayUnitToggle } from '@/shared/components/NetMassDisplayUnitToggle'
import { NetMassDisplayUnitProvider } from '@/shared/netMassDisplay/NetMassDisplayUnitContext'
import { useCallback, useMemo } from 'react'
import {
  buildCbamReportingDataExportFileName,
  cbamReportingPeriodConfig,
  getCbamReportStatus,
  getCbamPeriodInfo,
  type Quarter
} from '@/config/cbamReportingPeriods'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { useSession } from '@/shared/hooks/useSession'
import {
  cbamReportingBasicInfoCompleted,
  useDownloadStandaloneCbamReport,
  useExportCbamReportingData,
  useGetAllCbamReports
} from '@/features/cbam-reporting/hooks/cbamReports'
import CbamReportingStatusBadge from './components/CbamReportingStatusBadge'
import QuarterCountdownV2 from './components/QuarterCountdownV2'
import useDialog from '@/common/hooks/useDialog'
import ImportedGoodCreateDialog from './dialogs/ImportedGoodCreateDialog'
import ReopenCbamReportDialog from './dialogs/ReopenCbamReportDialog'
import ConfirmCompleteReportDialog from './dialogs/ConfirmCompleteReportDialog'
import ExportCbamReportingCsvDialog from './dialogs/ExportCbamReportingCsvDialog'
import toast from 'react-hot-toast'
import { useGetAllPartnerships } from '@/features/suppliers/hooks/partnerships'
import { useGetAllInstallationsForOrganization } from '@/features/suppliers/hooks/installations'
import ImportedGoodsUploadDialog from './components/ImportedGoodsUploadDialog'
import { DeclarantType, useGetDeclarantById } from '@/features/cbam-reporting/hooks/declarants'
import DeclarantDetailsIncompleteBanner from '@/features/cbam-reporting/components/OrganizationDetailsIncompleteBanner'
import { areAllImportedGoodsEligible } from '@/shared/utils/importedGoodEligibility'
import { getDefaultCbamModifiers } from '@/shared/types/cbamModifiers'
import { useCbamReportImportedGoods } from '../../hooks/useCbamReportImportedGoods'
import { useImportedGoodsWithLiability } from '../../hooks/useImportedGoodsWithLiability'
import CbamReportDetailsEstimatorView from './CbamReportDetailsEstimatorView'
import CbamReportDetailsLegacyView from './CbamReportDetailsLegacyView'
import { useGetAllImporters } from '@/features/importers/hooks/importers'
import { CalculatorIcon } from '@/common/components/icons'
import { useState } from 'react'

/** First reporting year that uses the Impact Estimator–aligned liability layout on report details. */
const CBAM_REPORTING_ESTIMATOR_LAYOUT_FROM_YEAR = 2026

function isCbamReportEstimatorLayoutYear(reportingPeriodYear: number): boolean {
  return reportingPeriodYear >= CBAM_REPORTING_ESTIMATOR_LAYOUT_FROM_YEAR
}

export default function CbamReportDetailsPage() {
  const { t, formatCbamTco2Display } = useI18n()
  const navigate = useNavigate()
  const [isEditVariablesOpen, setIsEditVariablesOpen] = useState(false)

  const { id: organizationId } = useSession().session.organization

  const cbamReportId = useParams<{
    cbamReportingId: string
  }>().cbamReportingId!
  const declarantId = useParams<{ declarantId: string }>().declarantId!

  const cbamReportsQuery = useGetAllCbamReports(organizationId)
  const { mutateAsync: downloadCbamReport, isPending: isDownloadingCbamReport } =
    useDownloadStandaloneCbamReport(organizationId, cbamReportId, 'zip')
  const { mutateAsync: exportReportingData, isPending: isExportingReportingData } =
    useExportCbamReportingData(organizationId, cbamReportId)
  const partnershipsQuery = useGetAllPartnerships(organizationId)
  const installationsQuery = useGetAllInstallationsForOrganization(organizationId)
  const declarantQuery = useGetDeclarantById(organizationId, declarantId)
  const importersQuery = useGetAllImporters(organizationId)

  const cbamReportForLiabilityHook = cbamReportsQuery.data?.find(
    report => report.id === cbamReportId
  )

  const reportingYearForLiabilityHook = cbamReportForLiabilityHook?.periodId
    ? cbamReportingPeriodConfig[cbamReportForLiabilityHook.periodId]?.year
    : undefined
  const partnerships = partnershipsQuery.data ?? []
  const installations = installationsQuery.data ?? []
  const importers = importersQuery.data ?? []
  const reportingPeriodYearForFilters = reportingYearForLiabilityHook ?? new Date().getFullYear()
  const getCountryLabel = useCallback(
    (countryCode: string) => t(`common:countriesByIsoAlpha2.${countryCode}`),
    [t]
  )
  const getAggregatedGoodsCategoryLabel = useCallback(
    (category: string) => t(`common:aggregatedGoods.${category}`),
    [t]
  )
  const getCnCodeLabel = useCallback((cnCode: string) => t(`goods:${cnCode}`), [t])
  const noneLabels = useMemo(
    () => ({
      noSupplier: 'cbamReporting:filters.noSupplier',
      noInstallation: 'cbamReporting:filters.noInstallation',
      noEmissionsReport: 'cbamReporting:filters.noEmissionsReport',
      noImporter: 'cbamReporting:filters.noImporter',
      noSeller: 'cbamReporting:filters.noSeller',
      noGrrFilename: 'cbamReporting:filters.noGrrFilename'
    }),
    []
  )
  const isRepresentative = declarantQuery.data?.type === DeclarantType.REPRESENTATIVE
  const useEstimatorBatchLiability =
    reportingYearForLiabilityHook != null &&
    isCbamReportEstimatorLayoutYear(reportingYearForLiabilityHook)

  const {
    importedGoodsExtended: goods,
    supplierEmissionsReports,
    filteredGoods,
    isLoadingImportedGoods,
    activeFilters,
    filterOptions,
    addFilter,
    removeFilter,
    clearAllFilters,
    hasActiveFilters,
    toggleFilters,
    setShowOnlyDefaultValues,
    setShowOnlyIncomplete
  } = useCbamReportImportedGoods(organizationId, cbamReportId, {
    partnerships,
    installations,
    importers,
    isRepresentative,
    reportingPeriodYear: reportingPeriodYearForFilters,
    getCountryLabel,
    getAggregatedGoodsCategoryLabel,
    getCnCodeLabel,
    noneLabels
  })

  const importedGoodsWithLiabilityOptions = useMemo(() => {
    if (!useEstimatorBatchLiability) {
      return { useEstimatorBatchLiability: false }
    }
    return {
      useEstimatorBatchLiability: true,
      modifiers: cbamReportForLiabilityHook?.modifiers ?? getDefaultCbamModifiers()
    }
  }, [useEstimatorBatchLiability, cbamReportForLiabilityHook?.modifiers])

  const {
    liabilityData,
    liabilityDataBaseLow,
    liabilityDataHighForScenario,
    isBatchLiabilityLoading
  } = useImportedGoodsWithLiability(
    filteredGoods,
    supplierEmissionsReports,
    importedGoodsWithLiabilityOptions
  )

  const declarant = declarantQuery.data ?? null
  const offPlatformPartnerships = partnerships.filter(partnership => partnership.isOffPlatform)
  const hasOffPlatformPartnerships = offPlatformPartnerships.length > 0
  const isLoadingDeclarant = declarantQuery.isLoading
  const isLoadingPartnerships = partnershipsQuery.isLoading
  const isLoadingInstallations = installationsQuery.isLoading

  const hasInstallations = installations.length > 0
  const hasInstallationsAndOffPlatformPartnerships = hasInstallations && hasOffPlatformPartnerships
  const areBasicInfoCompleted =
    declarant &&
    cbamReportingBasicInfoCompleted({
      city: declarant.city,
      identificationNumber: declarant.identificationNumber
    })
  const isReportingEnabled = areBasicInfoCompleted && hasInstallationsAndOffPlatformPartnerships

  const {
    open: isImportedGoodCreateDialogOpen,
    openDialog: openImportedGoodCreateDialog,
    closeDialog: closeImportedGoodCreateDialog
  } = useDialog()

  const {
    open: isImportedGoodsUploadDialogOpen,
    openDialog: openImportedGoodsUploadDialog,
    closeDialog: closeImportedGoodsUploadDialog
  } = useDialog()

  const {
    open: isConfirmCompleteReportDialogOpen,
    openDialog: openConfirmCompleteReportDialog,
    closeDialog: closeConfirmCompleteReportDialog
  } = useDialog()

  const {
    open: isReopenCbamReportDialogOpen,
    openDialog: openReopenCbamReportDialog,
    closeDialog: closeReopenCbamReportDialog
  } = useDialog()

  const {
    open: isExportCsvDialogOpen,
    openDialog: openExportCsvDialog,
    closeDialog: closeExportCsvDialog
  } = useDialog()

  const isLoadingCbamReports = cbamReportsQuery.isLoading

  const isLoading =
    isLoadingCbamReports ||
    isLoadingImportedGoods ||
    isLoadingPartnerships ||
    isLoadingInstallations ||
    isLoadingDeclarant

  const cbamReport = cbamReportsQuery.data?.find(cbamReport => cbamReport.id === cbamReportId)
  if (isLoading) {
    return <LoadingScreen />
  }

  if (!cbamReport) {
    return <Navigate to={`/cbam-reporting/${declarantId}`} />
  }

  const periodId = cbamReport.periodId

  const cbamReportConfig = {
    ...cbamReportingPeriodConfig[periodId],
    status: getCbamReportStatus(
      periodId,
      cbamReport.isCompleted,
      cbamReportingPeriodConfig[periodId].reportingEndDate
    )
  }

  const reportingPeriodYear = cbamReportConfig.year

  const totalCertificatesRequired = () => {
    if (reportingPeriodYear === 2025) {
      return '0 tCO₂'
    }
    return formatCbamTco2Display(
      liabilityData?.aggregatedProjection[reportingPeriodYear]?.netEmissions ?? 0
    )
  }

  const breadcrumbs = [
    { label: t('common:breadcrumbs.cbamReporting'), to: `/cbam-reporting/${declarantId}` },
    { label: cbamReportConfig.label, to: '' }
  ]

  const cbamPeriodState = getCbamPeriodInfo(periodId, cbamReport.isCompleted)

  const downloadReport = async () => {
    try {
      const report = await downloadCbamReport()
      const url = URL.createObjectURL(report)
      const link = document.createElement('a')
      link.href = url
      link.download = cbamReportConfig.composeFileName(cbamReport.version)
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)

      toast.success(t('cbamReporting:cbamReportDetails.notification.downloadReportSuccess'), {
        position: 'top-right'
      })
    } catch {
      toast.error(t('cbamReporting:cbamReportDetails.notification.downloadReportError'), {
        position: 'top-right'
      })
    }
  }

  const allImportedGoodsEligible = areAllImportedGoodsEligible(goods, isRepresentative)

  const isCbamCompleteEnabled = Boolean(
    isReportingEnabled && goods.length !== 0 && allImportedGoodsEligible
  )

  const isExportCsvEnabled = reportingPeriodYear === 2026
  const showEditVariablesBanner =
    isCbamReportEstimatorLayoutYear(reportingPeriodYear) && goods.length > 0
  const editVariablesTooltip = cbamReport.isCompleted
    ? t('cbamReporting:cbamReportDetails.estimator.editVariables.editVariablesDisabledTooltip')
    : undefined

  const handleExportDataConfirm = async (
    quarters: Quarter[],
    format: 'csv' | 'xlsx'
  ) => {
    try {
      const blob = await exportReportingData({ quarters, format })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = buildCbamReportingDataExportFileName(
        reportingPeriodYear,
        quarters,
        format
      )
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)

      toast.success(t('cbamReporting:cbamReportDetails.exportCsvDialog.notification.success'), {
        position: 'top-right'
      })
    } catch {
      toast.error(t('cbamReporting:cbamReportDetails.exportCsvDialog.notification.error'), {
        position: 'top-right'
      })
    }
  }

  return (
    <NetMassDisplayUnitProvider>
      <BreadcrumbsBar links={breadcrumbs} />
      <Container>
        <header className="flex justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <H2>{cbamReportConfig.label}</H2>
            <CbamReportingStatusBadge status={cbamReportConfig.status} />
          </div>
          <NetMassDisplayUnitToggle />
        </header>
        {showEditVariablesBanner && (
          <PromoBanner
            title={t('cbamReporting:cbamReportDetails.estimator.editVariablesBanner.title')}
            description={t(
              'cbamReporting:cbamReportDetails.estimator.editVariablesBanner.description'
            )}
            action={
              <Button
                icon={<CalculatorIcon className="size-6" />}
                onClick={() => setIsEditVariablesOpen(true)}
                disabled={cbamReport.isCompleted}
                tooltip={editVariablesTooltip}
              >
                {t('cbamReporting:cbamReportDetails.estimator.editVariablesBanner.action')}
              </Button>
            }
          />
        )}
        {!areBasicInfoCompleted && <DeclarantDetailsIncompleteBanner declarantId={declarantId} />}
        {!hasInstallationsAndOffPlatformPartnerships && (
          <PromoBanner
            title={t('cbamReporting:cbamReportDetails.noSuppliersAndInstallations.title')}
            description={t(
              'cbamReporting:cbamReportDetails.noSuppliersAndInstallations.description'
            )}
            action={
              <Button onClick={() => navigate('/suppliers')}>
                {t('cbamReporting:cbamReportDetails.noSuppliersAndInstallations.action')}
              </Button>
            }
          />
        )}
        <Card>
          <CardBody>
            <div className="flex items-center justify-between gap-2">
              <QuarterCountdownV2
                completed={cbamPeriodState.isCompleted}
                ongoing={cbamPeriodState.isOngoing}
                days={cbamPeriodState.daysLeft}
                submittedDate={cbamPeriodState.formattedEndDate}
                periodEndDate={cbamPeriodState.formattedPeriodEndDate}
                quarterDays={cbamPeriodState.totalDays}
                deadlinePassed={cbamPeriodState.deadlinePassed}
              />
              <div className="flex items-center gap-2">
                {cbamReport.isCompleted && (
                  <Button onClick={openReopenCbamReportDialog}>
                    {t('cbamReporting:cbamReportDetails.actions.reopenReport')}
                  </Button>
                )}
                {cbamReport.isCompleted ? (
                  <Button
                    isLoading={isDownloadingCbamReport}
                    variant="subtle"
                    className="w-52"
                    onClick={downloadReport}
                  >
                    {t('cbamReporting:cbamReportDetails.actions.downloadReport')}
                  </Button>
                ) : (
                  <Button
                    tooltip={
                      !isCbamCompleteEnabled
                        ? goods.length > 0 && !allImportedGoodsEligible
                          ? t(
                              'cbamReporting:cbamReportDetails.actions.completeReport.tooltipIncomplete'
                            )
                          : t('cbamReporting:cbamReportDetails.actions.completeReport.tooltip')
                        : undefined
                    }
                    disabled={!isCbamCompleteEnabled}
                    onClick={() => openConfirmCompleteReportDialog()}
                  >
                    {t('cbamReporting:cbamReportDetails.actions.completeReport.label')}
                  </Button>
                )}
              </div>
            </div>
          </CardBody>
        </Card>
        {isCbamReportEstimatorLayoutYear(reportingPeriodYear) ? (
          <CbamReportDetailsEstimatorView
            layout={{
              reportingPeriodYear,
              cbamReport,
              modifiers: cbamReport.modifiers ?? getDefaultCbamModifiers(),
              liabilityDataBaseLow,
              liabilityDataHighForScenario,
              isBatchLiabilityLoading,
              goods: filteredGoods,
              totalGoodsInReport: goods.length,
              isEditVariablesOpen,
              openEditVariables: () => setIsEditVariablesOpen(true),
              closeEditVariables: () => setIsEditVariablesOpen(false)
            }}
            importedGoods={{
              goods,
              partnerships,
              installations,
              supplierEmissionsReports,
              importers,
              liabilityData: liabilityData ?? null,
              filters: {
                activeFilters,
                filterOptions,
                filteredGoods,
                addFilter,
                removeFilter,
                clearAllFilters,
                hasActiveFilters,
                toggleFilters,
                setShowOnlyDefaultValues,
                setShowOnlyIncomplete
              },
              isRepresentative,
              isReportCompleted: cbamReport.isCompleted,
              organizationId,
              cbamReportId,
              modifiers: cbamReport.modifiers ?? getDefaultCbamModifiers(),
              onOpenUpload: openImportedGoodsUploadDialog,
              onOpenAddGoods: openImportedGoodCreateDialog,
              reportingPeriodYear: cbamReportConfig.year,
              periodId,
              isExportCsvEnabled,
              isExportCsvLoading: isExportingReportingData,
              onOpenExportCsv: openExportCsvDialog
            }}
          />
        ) : (
          <CbamReportDetailsLegacyView
            legacyLayout={{
              reportingPeriodYear,
              liabilityData,
              isBatchLiabilityLoading,
              goods: filteredGoods,
              totalCertificatesRequiredDisplay: totalCertificatesRequired()
            }}
            importedGoods={{
              goods,
              partnerships,
              installations,
              supplierEmissionsReports,
              importers,
              liabilityData: liabilityData ?? null,
              filters: {
                activeFilters,
                filterOptions,
                filteredGoods,
                addFilter,
                removeFilter,
                clearAllFilters,
                hasActiveFilters,
                toggleFilters,
                setShowOnlyDefaultValues,
                setShowOnlyIncomplete
              },
              isRepresentative,
              isReportCompleted: cbamReport.isCompleted,
              organizationId,
              cbamReportId,
              modifiers: cbamReport.modifiers ?? getDefaultCbamModifiers(),
              onOpenUpload: openImportedGoodsUploadDialog,
              onOpenAddGoods: openImportedGoodCreateDialog,
              reportingPeriodYear: cbamReportConfig.year,
              periodId,
              isExportCsvEnabled,
              isExportCsvLoading: isExportingReportingData,
              onOpenExportCsv: openExportCsvDialog
            }}
          />
        )}
      </Container>
      <ReopenCbamReportDialog
        isOpen={isReopenCbamReportDialogOpen}
        close={closeReopenCbamReportDialog}
        cbamReportId={cbamReportId}
      />
      <ImportedGoodsUploadDialog
        reportingPeriodYear={cbamReportConfig.year}
        isOpen={isImportedGoodsUploadDialogOpen}
        close={closeImportedGoodsUploadDialog}
        organizationId={organizationId}
        cbamReportId={cbamReportId}
        isRepresentative={isRepresentative}
      />
      <ConfirmCompleteReportDialog
        isOpen={isConfirmCompleteReportDialogOpen}
        close={closeConfirmCompleteReportDialog}
        organizationId={organizationId}
        cbamReportingId={cbamReportId}
        periodId={periodId}
      />
      <ExportCbamReportingCsvDialog
        isOpen={isExportCsvDialogOpen}
        close={closeExportCsvDialog}
        onConfirm={handleExportDataConfirm}
      />
      <ImportedGoodCreateDialog
        reportingPeriodYear={cbamReportConfig.year}
        periodId={periodId}
        isOpen={isImportedGoodCreateDialogOpen}
        close={closeImportedGoodCreateDialog}
        organizationId={organizationId}
        cbamReportId={cbamReportId}
        isRepresentative={isRepresentative}
      />
    </NetMassDisplayUnitProvider>
  )
}
